// ===================================================================
// WORKFLOWS — Module Layout with nav tabs and inbox badge
// ===================================================================

import { useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Group, Button, Text, Title, Divider, Badge } from '../../components/gab/GabComponents';
import { IconInbox, IconPlayerPlay, IconList, IconTemplate } from '@tabler/icons-react';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import { useWorkflowStore } from '../../store/workflowStore';
import { useAuthStore } from '../../store/authStore';
import { useRoleStore } from '../../store/roleStore';
import type { NavItem } from '../../types/nav';

const NAV_ITEMS: NavItem[] = [
  { label: 'Inbox',     path: '/workflows',           icon: IconInbox,       exact: true },
  { label: 'Active',    path: '/workflows/active',    icon: IconPlayerPlay },
  { label: 'All',       path: '/workflows/all',       icon: IconList },
  { label: 'Templates', path: '/workflows/templates', icon: IconTemplate,    minLevel: 'editor' },
];

const NAV_GROUPS: { label: string; paths: string[] }[] = [
  { label: '',      paths: ['/workflows', '/workflows/active', '/workflows/all'] },
  { label: 'Admin', paths: ['/workflows/templates'] },
];

export default function WorkflowLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const visibleItems = useFilteredNav('workflows', NAV_ITEMS);

  const user = useAuthStore(s => s.user);
  const { steps, instances, loadAll } = useWorkflowStore();

  useEffect(() => { loadAll(); }, []);

  // Count pending inbox items for current user (role-aware)
  const { roles, userRoles } = useRoleStore();
  const inboxCount = useMemo(() => {
    if (!user?.email) return 0;
    const email = user.email.toLowerCase();
    const userAssignment = userRoles.find(ur => (ur.userEmail || '').toLowerCase() === email);
    const userRole = userAssignment ? roles.find(r => String(r.id) === userAssignment.roleId) : undefined;

    return steps.filter(st => {
      if (st.status !== 'pending' && st.status !== 'in_progress') return false;
      const inst = instances.find(i => String(i.Rid) === st.instanceId);
      if (!inst || inst.status !== 'active') return false;
      if ((st.assignee || '').toLowerCase() === email) return true;
      if (st.assigneeType === 'role' && userRole) {
        const stepRole = (st.assignee || '').toLowerCase();
        if (userRole.roleSlug && stepRole === userRole.roleSlug.toLowerCase()) return true;
        if (userRole.roleName && stepRole === userRole.roleName.toLowerCase()) return true;
      }
      return false;
    }).length;
  }, [steps, instances, user?.email, roles, userRoles]);

  const groups = NAV_GROUPS.map(g => ({
    ...g,
    items: g.paths
      .map(p => visibleItems.find(vi => vi.path === p))
      .filter(Boolean) as typeof visibleItems,
  })).filter(g => g.items.length > 0);

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={3}>Workflows</Title>
          <Text size="sm" c="dimmed">Custom workflow engine with templates, approvals, and task tracking</Text>
        </div>
      </Group>

      <Group gap={6} mb="md" style={{ borderBottom: '1px solid var(--frost)', paddingBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {groups.map((group, gi) => (
          <Group key={gi} gap={4} style={{ alignItems: 'center' }}>
            {gi > 0 && <Divider orientation="vertical" style={{ height: 20, margin: '0 4px' }} />}
            {group.label && <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ marginRight: 2 }}>{group.label}</Text>}
            {group.items.map(item => {
              const active = item.exact
                ? pathname === item.path
                : pathname.startsWith(item.path);
              const isInbox = item.path === '/workflows';
              return (
                <Button
                  key={item.path}
                  variant={active ? 'filled' : 'light'}
                  color={active ? '#1E40AF' : 'gray'}
                  size="xs"
                  leftSection={<item.icon size={14} />}
                  rightSection={isInbox && inboxCount > 0 ? (
                    <Badge size="xs" variant="filled" color="red" circle>{inboxCount}</Badge>
                  ) : undefined}
                  onClick={() => navigate(item.path)}
                >
                  {item.label}
                </Button>
              );
            })}
          </Group>
        ))}
      </Group>
      <Outlet />
    </div>
  );
}
